import PageNavbar from "@/components/feature/PageNavbar";
import PageFooter from "@/components/feature/PageFooter";

const LAST_UPDATED = "July 13, 2026";

export default function RefundPage() {
  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
      <PageNavbar />

      <section className="relative w-full overflow-hidden" style={{ padding: "120px 40px 60px" }}>
        <div className="mx-auto" style={{ maxWidth: 1440 }}>
          <div style={{ fontSize: 13, color: "#8A8A8A", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 20 }}>
            Legal
          </div>
          <h1 style={{ fontSize: "clamp(36px, 5vw, 72px)", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.05, color: "#ECECEC", margin: 0, maxWidth: 900 }}>
            Refund Policy
          </h1>
          <p style={{ fontSize: 15, color: "#8A8A8A", marginTop: 16 }}>
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      <div className="mx-auto" style={{ maxWidth: 900, padding: "0 40px 120px" }}>
        <div style={{ fontSize: 15, color: "#8A8A8A", lineHeight: 1.85 }}>
          <Section title="1. General Policy">
            <p style={{ marginBottom: 16 }}>
              This Refund Policy explains the circumstances under which Intellivex LTD (&quot;Company&quot;, &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) may issue refunds for payments made directly through our website (the &quot;Site&quot;) or related services.
            </p>
            <p>
              We are committed to providing high-quality services and treating every customer fairly. All refund requests are reviewed individually in accordance with this Policy and applicable consumer protection laws.
            </p>
          </Section>

          <Section title="2. Eligibility for Refund">
            <p style={{ marginBottom: 16 }}>Refund requests may be considered when:</p>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}>A payment was made directly to the Company through the Site using a valid payment method.</li>
              <li style={{ marginBottom: 6 }}>A verified technical issue or service failure materially affected the delivery of the purchased service.</li>
              <li>The refund request is submitted within <strong style={{ color: "#ECECEC" }}>14 calendar days</strong> of the original payment date.</li>
            </ul>
            <p style={{ marginBottom: 16 }}>
              All refund requests are evaluated on a case-by-case basis. Submission of a request does not guarantee approval.
            </p>
            <p style={{ marginBottom: 16 }}>Refunds will generally <strong style={{ color: "#ECECEC" }}>not</strong> be provided in the following situations:</p>
            <ul style={{ listStyle: "disc", paddingLeft: 20 }}>
              <li style={{ marginBottom: 6 }}>Change of mind after purchase.</li>
              <li style={{ marginBottom: 6 }}>General dissatisfaction where the service was provided as described.</li>
              <li style={{ marginBottom: 6 }}>Failure to use or access the purchased service.</li>
              <li style={{ marginBottom: 6 }}>User error or misunderstanding of the Service.</li>
              <li style={{ marginBottom: 6 }}>Issues caused by third-party services or circumstances beyond the Company&apos;s reasonable control.</li>
              <li>Requests submitted after the applicable refund period.</li>
            </ul>
          </Section>

          <Section title="3. How to Request a Refund">
            <p style={{ marginBottom: 4 }}>To request a refund, please contact our support team:</p>
            <p style={{ marginBottom: 4 }}>Email: support@inntellivex.com</p>
            <p style={{ marginBottom: 16 }}>Phone: +44 7520 680012</p>
            <p style={{ marginBottom: 16 }}>To help us process your request efficiently, please include:</p>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}>Your full name.</li>
              <li style={{ marginBottom: 6 }}>Your contact information.</li>
              <li style={{ marginBottom: 6 }}>Proof of payment (transaction date, amount, and reference number, if available).</li>
              <li>A detailed description of the issue and the reason for requesting a refund.</li>
            </ul>
            <p>
              We may request additional information where reasonably necessary to verify your request.
            </p>
          </Section>

          <Section title="4. Review Process">
            <p style={{ marginBottom: 16 }}>Each refund request is reviewed individually by our support team.</p>
            <p style={{ marginBottom: 16 }}>The Company reserves the right to:</p>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginBottom: 16 }}>
              <li style={{ marginBottom: 6 }}>Approve or deny any refund request.</li>
              <li style={{ marginBottom: 6 }}>Request additional supporting information or documentation.</li>
              <li>Determine whether the request qualifies under this Refund Policy.</li>
            </ul>
            <p>
              Our decision will be communicated to you within a reasonable period after all necessary information has been received.
            </p>
          </Section>

          <Section title="5. Refund Processing">
            <p style={{ marginBottom: 16 }}>If a refund is approved:</p>
            <ul style={{ listStyle: "disc", paddingLeft: 20 }}>
              <li style={{ marginBottom: 6 }}>The refund will be issued to the original payment method whenever possible.</li>
              <li style={{ marginBottom: 6 }}>Processing times typically range from <strong style={{ color: "#ECECEC" }}>5 to 14 business days</strong>, depending on your payment provider or financial institution.</li>
              <li>Any applicable taxes or fees will be handled in accordance with applicable law.</li>
            </ul>
          </Section>

          <Section title="6. Cancellations">
            <p style={{ marginBottom: 16 }}>
              Where applicable, you may cancel future services or recurring billing at any time.
            </p>
            <p style={{ marginBottom: 16 }}>Please note that:</p>
            <ul style={{ listStyle: "disc", paddingLeft: 20 }}>
              <li style={{ marginBottom: 6 }}>Cancellation prevents future charges only.</li>
              <li style={{ marginBottom: 6 }}>Cancellation does not automatically entitle you to a refund for payments already made.</li>
              <li>Any refund remains subject to the eligibility requirements set out in this Policy.</li>
            </ul>
          </Section>

          <Section title="7. Limitation of Liability">
            <p style={{ marginBottom: 16 }}>The Company is not responsible for losses or issues resulting from:</p>
            <ul style={{ listStyle: "disc", paddingLeft: 20 }}>
              <li style={{ marginBottom: 6 }}>User devices, software, or internet connectivity.</li>
              <li style={{ marginBottom: 6 }}>Interruptions caused by third-party providers or external services.</li>
              <li>Events beyond the Company&apos;s reasonable control, including force majeure events.</li>
            </ul>
          </Section>

          <Section title="8. Changes to this Refund Policy">
            <p style={{ marginBottom: 16 }}>
              We may update this Refund Policy from time to time to reflect changes in our business practices, legal requirements, or services.
            </p>
            <p style={{ marginBottom: 16 }}>
              The latest version will always be published on this page with the updated effective date.
            </p>
            <p>
              Your continued use of the Site following any changes constitutes your acceptance of the revised Refund Policy.
            </p>
          </Section>

          <Section title="9. Contact Information">
            <p style={{ marginBottom: 8 }}>If you have any questions regarding this Refund Policy, please contact us:</p>
            <p style={{ marginBottom: 4 }}>Intellivex LTD</p>
            <p style={{ marginBottom: 4 }}>50 Essex Street</p>
            <p style={{ marginBottom: 4 }}>London</p>
            <p style={{ marginBottom: 4 }}>WC2R 3JF</p>
            <p style={{ marginBottom: 4 }}>United Kingdom</p>
            <p style={{ marginBottom: 4 }}>Email: support@inntellivex.com</p>
            <p>Phone: +44 7520 680012</p>
          </Section>
        </div>
      </div>

      <PageFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 48 }}>
      <h2 style={{ fontSize: 22, fontWeight: 600, color: "#ECECEC", letterSpacing: "-0.02em", marginBottom: 16 }}>
        {title}
      </h2>
      <div>{children}</div>
    </div>
  );
}